import React, { useCallback, useEffect, useState } from 'react'
import { AnnotationDetailModel, MessageDetailModel, RequestDetailModel } from '../requestsModel'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHistory, useRouteMatch } from 'react-router'
import Loader from '../../../components/loader/Loader'
import fetchFactory from '../../../utils/fetchFactory'
import errorCatcher from '../../../utils/errorCatcher'
import { connect } from 'react-redux'
import Button from '../../../components/button/Button'
import FooterNewDemande from '../FooterNewDemande'
import Link from '../../../components/link/Link'
import { convertRequestDetail, getRequestCategory } from '../requestMapper'
import { useMediaQuery } from 'react-responsive'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import styles from '../../../styles'
import { Administrative, Technical } from '../../../icons'
import { formatDate } from '../../../utils/formatDate'
import AnnotationItem from './AnnotationItem'
import ReponseDetail from './ReponseDetail'
import NotFound from '../../../components/notfound/NotFound'
import { displayModal } from '../../reducers/modal/actions'
import { modalTypes } from '../../../enums/modalTypeEnum'
import { timeSlotEnum, weekDaysEnum } from '../../../enums/choicesEnum'
import { ASSURANCE_CATEGORY, LEAVE_DEPOSIT_CATEGORY } from '../list/RequestCard'

const REQUEST_DETAIL_ERROR = 'get_request_detail'

const useStyle = makeStyles(
	(theme) => (
		{
			...styles(theme),
			detailCard: {
				background: 'white',
				marginBottom: 16,

				'& div p': {
					margin: 0
				}
			},
			detailCardLargeScreen: {
				padding: '35px 35px 35px 50px'
			},
			detailCardSmallScreen: {
				padding: 18
			},
			titleContainer: {
				display: 'flex'
			},
			infoColumn: {
				display: 'flex',
				flexDirection: 'row-reverse'
			},
			tuileContainer: {
				minHeight: 32,
				height: 'fit-content',
				borderRadius: 15,
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				textAlign: 'center'
			},
			dateContainer: {
				backgroundColor: theme.palette.blue['10']
			},
			reponseContainer: {
				backgroundColor: theme.palette.accessibility.main,
				color: 'white',
				marginLeft: 10

			},
			infoText: {
				margin: '2px 20px 2px 20px'
			},
			backLink: {
				marginLeft: '-.5rem',
				marginBottom: 20
			},
			titleReponseContainer: {
				paddingBottom: 10,
				marginTop: 73
			},
			contenuReponseContainerLargeScreen: {
				marginBottom: 115,
				whiteSpace: 'pre-line'
			},
			contenuReponseContainerSmallScreen: {
				marginBottom: 20,
				whiteSpace: 'pre-line'
			},
			autoMargin: {
				margin: 'auto'
			},
			marginSmallScreen: {
				marginTop: 20
			},
			rowReversed: {
				flexDirection: 'row-reverse'
			},
			ltr: {
				direction: 'ltr'
			},
			hr: {
				borderBottom: `${theme.palette.accessibility.main} 1px solid`,
				width: '100%',
				marginLeft: '.5rem',
				marginBottom: 48
			},
			textCreneau: {
				paddingTop: 10,
				paddingBottom: 10
			},
			textOptionContainer: {
				marginTop: 20
			}
		}
	)
)

interface MatchParamsModel {
	id: string
}

interface Store {
	errorCatcher: (error: any, id: string) => void
	displayModal: (modalType: string, content: any) => void
}

type RequestDetailProps = Store

const RequestDetail: React.FC<RequestDetailProps> = (
	{
		errorCatcher,
		displayModal
	}
) => {
	const classes = useStyle()
	const history = useHistory()
	const intl = useIntl()
	const match = useRouteMatch<MatchParamsModel>()
	const theme = useTheme()

	const [request, setRequest] = useState<RequestDetailModel | null>(null)
	const [loading, setLoading] = useState<boolean>(true)

	const fetchData = useCallback(() => {
		setLoading(true)
		fetchFactory(
			`/api/ec_${process.env.REACT_APP_TARGET}/demande/getDemandeDetails?request_id=${match.params.id}`,
			{
				method: 'GET'
			}
		)
			.then(
				(response: any) => {
					if (response && response.value && response.value.length) {
						setRequest(convertRequestDetail(response.value[0]))
					}
				}
			)
			.catch(
				(error) => {
					errorCatcher(error, REQUEST_DETAIL_ERROR)
				}
			)
			.finally(
				() => {
					setLoading(false)
				}
			)
	}, [match.params.id, errorCatcher])

	useEffect(() => {
		if (match.params.id) {
			fetchData()
		} else {
			setLoading(false)
		}
	}, [match.params.id, fetchData])

	const goBack = () => {
		history.goBack()
	}

	const isLargeScreen = useMediaQuery(
		{
			query: '(min-width: 1025px)'
		}
	)

	const detailCardClass: ClassValue = classNames(
		'row middle-xs between-xs',
		classes.detailCard,
		{
			[classes.detailCardLargeScreen]: isLargeScreen,
			[classes.detailCardSmallScreen]: !isLargeScreen
		}
	)

	const titleClass: ClassValue = classNames(
		'col-xs-8 col-md-7',
		classes.titleContainer,
		classes.colorBlue,
		isLargeScreen ? classes.title1 : classes.title2
	)

	const categoryClass: ClassValue = classNames(
		'col-xs-12',
		classes.colorBlue,
		classes.text0_opacity,
		{ [classes.marginSmallScreen]: !isLargeScreen }
	)

	const titleReponseClass: ClassValue = classNames(
		'col-xs-10',
		classes.titleReponseContainer,
		classes.title8,
		classes.colorBlue,
		{ [classes.marginSmallScreen]: !isLargeScreen }
	)

	const contenuReponseClass: ClassValue = classNames(
		'col-xs-10',
		isLargeScreen ? classes.contenuReponseContainerLargeScreen : classes.contenuReponseContainerSmallScreen,
		classes.text0,
		classes.colorBlue
	)

	const btnClass: ClassValue = classNames(
		'col-xs-6 col-md-4',
		classes.autoMargin
	)

	const answerClass: ClassValue = classNames(
		classes.infoText,
		isLargeScreen ? classes.text4 : classes.text6
	)

	const dateClass: ClassValue = classNames(
		classes.infoText,
		isLargeScreen ? classes.text4_opacity : classes.text6_opacity
	)

	const headerClass: ClassValue = classNames(
		'col-xs-12 row',
		{ [classes.rowReversed]: !isLargeScreen }
	)

	const infoClass: ClassValue = classNames(
		'col-md-4',
		classes.infoColumn,
		{ [classes.marginSmallScreen]: !isLargeScreen }
	)

	const iconClass: ClassValue = classNames(
		'col-xs-4 col-md-1',
		{ [classes.ltr]: !isLargeScreen }
	)

	const annotationClass : ClassValue = classNames(
		classes.text2,
		classes.colorBlue,
		classes.medium
	)

	const iconProp = {
		color: theme.palette.blue.main,
		width: 50,
		height: 50
	}

	if (loading) {
		return <Loader />
	}

	if (!request || !request.messages.length) {
		return <NotFound />
	}

	let labelCategory = ''
	let subCategory = ''
	let isTechnical = false
	let isPrivate = false

	// Gestion du display de la catégorie
	if (request.category === LEAVE_DEPOSIT_CATEGORY) {
		labelCategory = `${intl.formatMessage({
			id: 'requests.makeRequest.subHeader.administrative',
			defaultMessage: 'Demande administrative',
			description: 'Administrative label'
		})} - ${intl.formatMessage({
			id: 'requests.makeRequest.step2.contract',
			defaultMessage: 'Vie du contrat',
			description: 'Contract label'
		})} - ${intl.formatMessage({
			id: 'requests.makeRequest.step3.deposit',
			defaultMessage: 'Résiliation du bail',
			description: 'Leave deposit label'
		})}`
	} else if (request.category === ASSURANCE_CATEGORY) {
		labelCategory = `${intl.formatMessage({
			id: 'requests.makeRequest.subHeader.administrative',
			defaultMessage: 'Demande administrative',
			description: 'Administrative label'
		})} - ${intl.formatMessage({
			id: 'requests.makeRequest.step2.contract',
			defaultMessage: 'Vie du contrat',
			description: 'Contract label'
		})} - ${intl.formatMessage({
			id: 'requests.makeRequest.step3.assurance',
			defaultMessage: 'Attestation d\'assurance habitation',
			description: 'Assurance label'
		})}`
	} else {
		const requestCategory = getRequestCategory(request.category, intl)
		subCategory = requestCategory.subCategory
		labelCategory = requestCategory.labelCategory
		isTechnical = requestCategory.isTechnical
		isPrivate = requestCategory.isPrivate
	}

	// Gestion du badge de date
	let updateLabelProps = {
		id: 'requests.createdAt',
		defaultMessage: 'Créée',
		description: 'Date de création'
	}
	if (request.cloture) {
		updateLabelProps = {
			id: 'requests.closedAt',
			defaultMessage: 'Clôturée',
			description: 'Date de clôture'
		}
	} else if (request.annule) {
		updateLabelProps = {
			id: 'requests.canceledAt',
			defaultMessage: 'Annulée',
			description: 'Date d\'annulation'
		}
	} else if (request.messages.length > 1) {
		updateLabelProps = {
			id: 'requests.updatedAt',
			defaultMessage: 'Mise à jour',
			description: 'Date de mise à jour'
		}
	}

	const firstMessage = request.messages[0]

	const choice1: string = intl.formatMessage({
		id: 'requests.detail.keyOptionChosen',
		defaultMessage: 'Vous avez choisi de déposer un exemplaire de vos clés au bureau d\'accueil de votre YouFirst Manager',
		description: 'key option text'
	})
	const choice2: string = intl.formatMessage({
		id: 'requests.detail.datesOptionChosen',
		defaultMessage: 'Vous avez choisi les créneaux de disponibilité suivants :',
		description: 'date option text'
	})
	const choice3: string = intl.formatMessage({
		id: 'requests.detail.advisorOptionChosen',
		defaultMessage: 'Vous avez choisi d\'être contacté par votre YouFirst Manager',
		description: 'advisor option text'
	})

	const textOptionClass: ClassValue = classNames(
		isLargeScreen ? [classes.text11] : [classes.text18],
		classes.colorBlue
	)
	const containerChoicesDatesClass: ClassValue = classNames(
		classes.flexColumn,
		classes.flex
	)

	const textCreneauClass: ClassValue = classNames(
		classes.text1,
		classes.colorBlue,
		classes.textCreneau
	)

	const textOptionContainerClass: ClassValue = classNames(
		classes.textOptionContainer,
		'col-xs-12'
	)

	return <div>
		<div className={classes.backLink}>
			<Link
				color="colorAccessibility"
				textStyle="text4"
				onClick={goBack}
				hasChevron={false}
				hasChevronLeft
			>
				<FormattedMessage
					id="requests.detail.back"
					defaultMessage="Toutes vos demandes"
					description="Retour sur la liste des demandes"
				/>
			</Link>
		</div>
		<div className={detailCardClass}>
			<div className={headerClass}>
				<div className={iconClass}>
					{isTechnical ? <Technical {...iconProp} /> : <Administrative {...iconProp} />}
				</div>
				<div className={titleClass}>
					<FormattedMessage
						id="requests.detail.title"
						defaultMessage="Votre demande"
						description="Votre demande"
					/>
				</div>

				<div className={infoClass}>
					{request.messages.length > 1 && <div className={classNames(classes.reponseContainer, classes.tuileContainer)}>
            <span className={answerClass}>{request.messages.length - 1} <FormattedMessage
	            id="requests.detail.nbAnswers"
	            defaultMessage="Nb réponses"
	            description="Nombre de réponse"
	            values={
		            {
			            nbAnswers: request.messages.length - 1
		            }
	            }
            />
            </span>
					</div>}
					<div className={classNames(classes.dateContainer, classes.tuileContainer)}>
          <span className={dateClass}><FormattedMessage
	          {...updateLabelProps}
	          values={{
		          date: formatDate(request.messages[request.messages.length - 1].modifiedOn, intl.locale)
	          }}
          />
          </span>
					</div>
				</div>
			</div>
			<div className={categoryClass}>
				{labelCategory}
			</div>
			<div className={titleReponseClass}>
				{subCategory}
			</div>
			<hr className={classes.hr} />
			<div className={contenuReponseClass}>
				{firstMessage.message}
			</div>
			{firstMessage.annotation.length > 0 && (<div className="col-xs-12">
				<div className={annotationClass}>
					<FormattedMessage
						id="requests.detail.myAnnotations"
						defaultMessage="Vous avez joint des fichiers"
						description="Fichiers joints"
						values={{
							nbAnnotations: firstMessage.annotation.length
						}}
					/>
				</div>
				{firstMessage.annotation.map((item: AnnotationDetailModel, index: number) =>
					<div key={`${firstMessage.id}-annotation-${index}`}>
						<AnnotationItem annotation={item} />
					</div>
				)}
			</div>)}
			{
				isTechnical && isPrivate &&
				<div className={textOptionContainerClass}>
					{
						request.clefalaloge ?
							<span className={textOptionClass}>
								{choice1}
							</span>
							:
							(
								request.creneau1_jour && request.creneau1_timeslot && request.creneau2_jour && request.creneau1_timeslot ?
									<div className={containerChoicesDatesClass}>
										<span className={textOptionClass}>
											{choice2}
										</span>
										<div className={containerChoicesDatesClass}>
											<span className={textCreneauClass}>
												<FormattedMessage
													id={`requests.weekDays.${weekDaysEnum[request.creneau1_jour as keyof typeof weekDaysEnum]}`}
													defaultMessage={request.creneau1_jour}
													description={`${request.creneau1_jour} weekDay`}
												/>
												<span>,&nbsp;</span>
												<FormattedMessage
													id={`requests.timeSlots.${timeSlotEnum[request.creneau1_timeslot as keyof typeof timeSlotEnum]}`}
													defaultMessage={request.creneau1_timeslot}
													description={`${request.creneau1_timeslot} timeSlot`}
												/>
											</span>
											<span className={textCreneauClass}>
												<FormattedMessage
													id={`requests.weekDays.${weekDaysEnum[request.creneau2_jour as keyof typeof weekDaysEnum]}`}
													defaultMessage={request.creneau2_jour}
													description={`${request.creneau2_jour} weekDay`}
												/>
												<span>,&nbsp;</span>
												<FormattedMessage
													id={`requests.timeSlots.${timeSlotEnum[request.creneau2_timeslot as keyof typeof timeSlotEnum]}`}
													defaultMessage={request.creneau2_timeslot}
													description={`${request.creneau2_timeslot} timeSlot`}
												/>
											</span>
											{request.creneau3_jour && request.creneau3_timeslot &&
											<span className={textCreneauClass}>
												<FormattedMessage
													id={`requests.weekDays.${weekDaysEnum[request.creneau3_jour as keyof typeof weekDaysEnum]}`}
													defaultMessage={request.creneau3_jour}
													description={`${request.creneau3_jour} weekDay`}
												/>
												<span>,&nbsp;</span>
												<FormattedMessage
													id={`requests.timeSlots.${timeSlotEnum[request.creneau3_timeslot as keyof typeof timeSlotEnum]}`}
													defaultMessage={request.creneau3_timeslot}
													description={`${request.creneau3_timeslot} timeSlot`}
												/>
											</span>
											}
										</div>
									</div>
									:
									<span className={textOptionClass}>
										{choice3}
									</span>
							)
					}
				</div>
			}
		</div>
		{request.messages.length > 1 && request.messages.filter((reponse: MessageDetailModel, index: number) => index > 0).map((reponse: MessageDetailModel, index: number) =>
			<ReponseDetail key={`${request.id}-reponse-${index}`} reponse={reponse} />
		)}

		{!(request.annule || request.cloture) && <div className={btnClass}>
			<Button
				background="backgroundBlue"
				color="colorWhite"
				textStyle="text8"
				onClick={() => displayModal(modalTypes.response, { title: subCategory, id: request.id, callback: fetchData })}
			>
				<FormattedMessage
					id="requests.detail.btnAnswer"
					defaultMessage="Écrire votre message"
					description="Answer button"
				/>
			</Button>
		</div>}
		<FooterNewDemande detailView />
	</div>
}

const mapDispatchToProps = {
	displayModal,
	errorCatcher
}

export default connect(undefined, mapDispatchToProps)(RequestDetail)
